import { getName } from "country-list";
import {
  Confidence,
  DayNight,
  IncidentCardLink,
  IncidentType,
} from "../../types";

export const getConfidenceLabel = (
  confidence: Confidence | undefined,
): string | null => {
  if (confidence === "H") return "high";
  if (confidence === "N") return "nominal";
  if (confidence === "L") return "low";

  return null;
};

export const getDayNightLabel = (
  dayNight: DayNight | undefined,
): string | null => {
  if (dayNight === "D") return "daytime";
  if (dayNight === "N") return "night-time";

  return null;
};

const normaliseCountryCode = (countryCode: string): string | null => {
  const trimmedCountryCode = countryCode.trim().toUpperCase();

  if (!trimmedCountryCode) return null;

  return getName(trimmedCountryCode) ?? null;
};

const mapCountryCodesToNames = (countries: Array<unknown>): Array<string> => {
  return compact(
    countries.map((country) => {
      if (typeof country !== "string") return null;

      return normaliseCountryCode(country);
    }),
  );
};

export const normaliseAffectedCountries = (value: unknown): Array<string> => {
  if (Array.isArray(value)) return mapCountryCodesToNames(value);

  if (typeof value === "string") {
    try {
      const parsedValue: unknown = JSON.parse(value);

      if (Array.isArray(parsedValue))
        return mapCountryCodesToNames(parsedValue);
    } catch {
      return mapCountryCodesToNames(value.split(","));
    }
  }

  return [];
};

export const compact = (
  values: Array<string | null | undefined>,
): Array<string> =>
  values.filter((value): value is string => Boolean(value && value.trim()));

export const joinSentences = (
  values: Array<string | null | undefined>,
): string | null => {
  const sentences = compact(values);

  if (!sentences.length) return null;

  return sentences.join(" ");
};

const isValidNumber = (value: number | undefined): value is number =>
  Number.isFinite(value);

export const formatDisplayValue = (
  value: number | undefined | "N/A" | IncidentType,
): string => {
  return value === undefined ? "N/A" : value.toLocaleString();
};

export const formatNumber = (
  value: number | undefined,
  maximumFractionDigits = 1,
): string | null => {
  if (!isValidNumber(value)) return null;

  return new Intl.NumberFormat("en-GB", { maximumFractionDigits }).format(
    value,
  );
};

export const normaliseTimestamp = (
  value: string | number | undefined | null,
): number | null => {
  if (value === undefined || value === null || value === "") return null;

  const timestamp = typeof value === "number" ? value : Date.parse(value);

  if (!Number.isFinite(timestamp)) return null;

  return timestamp > 0 && timestamp < 100000000000
    ? timestamp * 1000
    : timestamp;
};

export const formatDate = (
  value: string | number | undefined,
): string | null => {
  const timestamp = normaliseTimestamp(value);

  if (!timestamp) return null;

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

export const formatDateTime = (
  value: string | number | undefined,
): string | null => {
  const timestamp = normaliseTimestamp(value);

  if (!timestamp) return null;

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
};

export const formatList = (values: Array<string>): string => {
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;

  return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
};

export const normaliseUrl = (value: string | undefined): string | null => {
  if (!value?.trim()) return null;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;

  return `https://${value}`;
};

export const createLinks = (
  links: Array<IncidentCardLink | null | undefined>,
): Array<IncidentCardLink> =>
  links.filter((link): link is IncidentCardLink =>
    Boolean(link?.href && link.label),
  );

export const createLink = (
  label: string,
  href: string | undefined,
): IncidentCardLink | null => {
  const normalisedHref = normaliseUrl(href);

  if (!normalisedHref) return null;

  return {
    label,
    href: normalisedHref,
  };
};

export const formatShortDate = (
  value: string | number | undefined,
): string | null => {
  const timestamp = normaliseTimestamp(value);

  if (!timestamp) return null;

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

export const formatShortDateTime = (
  value: string | number | undefined,
): string | null => {
  const timestamp = normaliseTimestamp(value);

  if (!timestamp) return null;

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return null;

  const formattedDate = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
  const formattedTime = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(date);

  return `${formattedDate} at ${formattedTime}`;
};
